import { useReducedMotion } from "@src/packages/moon-ui/hooks/useReducedMotion";
import { useCallback, useEffect, useRef, useState } from "react";
import { Animated, Image, StyleSheet, Text, View } from "react-native";
import { LinearGradient } from "expo-linear-gradient";
import { BlurView } from "expo-blur";
import { Phone, RotateCcw } from "lucide-react-native";
import {
  Dialog,
  DialogHeader,
  FieldMessage,
  MoonPhoneField,
  TextActionButton,
  LoadingState,
} from "@louise/moon-ui";
import { OTPInput } from "@src/packages/form/OTPInput/OTPInput";
import { useTranslations } from "@src/packages/i18n";
import { useApiClient } from "@src/packages/api/louise-api";
import { useUserCountry } from "@src/packages/location/useUserCountry";
import { useDismissible } from "@src/packages/homepage/utils/useDismissible";
import {
  usePatient,
  useUpdatePatient,
} from "@src/packages/patients/hooks/usePatient";
import {
  findMoonPhoneCountry,
  MOON_PHONE_COUNTRIES,
  type MoonPhoneCountry,
} from "@src/packages/moon-ui/phoneCountries";
import { useStytch, useStytchUser } from "../../utils/useStytch";
import { useIsFocused } from "@react-navigation/native";
import { OnboardingPrimaryButton } from "../../onboarding/components/OnboardingLayout";
import { semanticColors } from "@louise/theme/tokens";

const DISMISSAL_KEY = "home/oauth-phone-link";
const PROMPT_IDLE_DELAY_MS = 30_000;
const PROMPT_RETURN_DELAY_MS = 1_500;

const ONBOARDING_CELLULAR_BACKGROUND = require("../../assets/images/onboarding-cellular-bg.jpg");
const DEFAULT_PHONE_COUNTRY = findMoonPhoneCountry("US")!;
const SUPPORTED_PHONE_COUNTRY_CODES = MOON_PHONE_COUNTRIES.map(
  ({ iso }) => iso,
);

type PromptStep = "phone" | "otp" | "success";

function TextingExchangeAnimation() {
  const reducedMotion = useReducedMotion();
  const firstMessage = useRef(new Animated.Value(0)).current;
  const typingMessage = useRef(new Animated.Value(0)).current;
  const replyMessage = useRef(new Animated.Value(0)).current;
  const t = useTranslations();

  useEffect(() => {
    firstMessage.setValue(reducedMotion ? 1 : 0);
    typingMessage.setValue(0);
    replyMessage.setValue(reducedMotion ? 1 : 0);
    if (reducedMotion) return;
    const animation = Animated.loop(
      Animated.sequence([
        Animated.delay(220),
        Animated.timing(firstMessage, {
          toValue: 1,
          duration: 320,
          useNativeDriver: true,
        }),
        Animated.delay(360),
        Animated.timing(typingMessage, {
          toValue: 1,
          duration: 180,
          useNativeDriver: true,
        }),
        Animated.delay(620),
        Animated.timing(typingMessage, {
          toValue: 0,
          duration: 180,
          useNativeDriver: true,
        }),
        Animated.timing(replyMessage, {
          toValue: 1,
          duration: 320,
          useNativeDriver: true,
        }),
        Animated.delay(1500),
        Animated.parallel([
          Animated.timing(firstMessage, {
            toValue: 0,
            duration: 240,
            useNativeDriver: true,
          }),
          Animated.timing(replyMessage, {
            toValue: 0,
            duration: 240,
            useNativeDriver: true,
          }),
        ]),
      ]),
    );

    animation.start();
    return () => animation.stop();
  }, [firstMessage, replyMessage, typingMessage, reducedMotion]);

  const firstMessageStyle = {
    opacity: firstMessage,
    transform: [
      {
        translateY: firstMessage.interpolate({
          inputRange: [0, 1],
          outputRange: [8, 0],
        }),
      },
    ],
  };
  const typingMessageStyle = {
    opacity: typingMessage,
    transform: [
      {
        translateY: typingMessage.interpolate({
          inputRange: [0, 1],
          outputRange: [6, 0],
        }),
      },
    ],
  };
  const replyMessageStyle = {
    opacity: replyMessage,
    transform: [
      {
        translateY: replyMessage.interpolate({
          inputRange: [0, 1],
          outputRange: [8, 0],
        }),
      },
    ],
  };

  return (
    <View
      pointerEvents="none"
      style={{
        width: 192,
        height: 92,
        alignSelf: "center",
        marginBottom: 20,
      }}
    >
      <Animated.View
        style={[
          {
            position: "absolute",
            top: 0,
            right: 0,
            maxWidth: 150,
            paddingHorizontal: 13,
            paddingVertical: 8,
            borderRadius: 16,
            borderTopRightRadius: 6,
            backgroundColor: "#0A84FF",
          },
          firstMessageStyle,
        ]}
      >
        <Text
          className="font-dm-sans"
          style={{
            color: "#FFFFFF",
            fontSize: 12,
            lineHeight: 16,
            fontWeight: "400",
          }}
        >
          {t("home.oauth_phone_link.message_user_question")}
        </Text>
      </Animated.View>

      <Animated.View
        style={[
          {
            position: "absolute",
            top: 52,
            left: 0,
            flexDirection: "row",
            gap: 4,
            paddingHorizontal: 12,
            paddingVertical: 9,
            borderRadius: 15,
            backgroundColor: "#3A3A3C",
          },
          typingMessageStyle,
        ]}
      >
        {[0, 1, 2].map((dot) => (
          <View
            key={dot}
            style={{
              width: 4,
              height: 4,
              borderRadius: 2,
              backgroundColor: "rgba(255, 255, 255, 0.78)",
            }}
          />
        ))}
      </Animated.View>

      <Animated.View
        style={[
          {
            position: "absolute",
            left: 0,
            bottom: 0,
            maxWidth: 150,
            paddingHorizontal: 13,
            paddingVertical: 8,
            borderRadius: 16,
            borderBottomLeftRadius: 6,
            backgroundColor: "#3A3A3C",
          },
          replyMessageStyle,
        ]}
      >
        <Text
          className="font-dm-sans"
          style={{
            color: "#FFFFFF",
            fontSize: 12,
            lineHeight: 16,
            fontWeight: "400",
          }}
        >
          {t("home.oauth_phone_link.message_concierge_reply")}
        </Text>
      </Animated.View>
    </View>
  );
}

function hasGoogleOrAppleProvider(
  providers: { provider_type: string }[] | undefined,
): boolean {
  return (
    providers?.some((provider) => {
      const providerType = provider.provider_type.toLowerCase();
      return providerType === "google" || providerType === "apple";
    }) ?? false
  );
}

function isPromptFixtureEnabled(): boolean {
  if (!__DEV__ || typeof window === "undefined") return false;
  try {
    return (
      new URLSearchParams(window.location.search).get(
        "dev_oauth_phone_link_prompt",
      ) === "1"
    );
  } catch {
    return false;
  }
}

function getPromptFixtureValue(key: string): string {
  if (!isPromptFixtureEnabled() || typeof window === "undefined") return "";
  try {
    return new URLSearchParams(window.location.search).get(key) ?? "";
  } catch {
    return "";
  }
}

function isPromptOtpFixtureEnabled(): boolean {
  return getPromptFixtureValue("dev_oauth_phone_link_step") === "otp";
}

function isPromptSuccessFixtureEnabled(): boolean {
  return getPromptFixtureValue("dev_oauth_phone_link_step") === "success";
}

export default function OAuthPhoneLinkPrompt() {
  const t = useTranslations();
  const promptFixtureEnabled = isPromptFixtureEnabled();
  const promptOtpFixtureEnabled = isPromptOtpFixtureEnabled();
  const promptSuccessFixtureEnabled = isPromptSuccessFixtureEnabled();
  const promptFixturePhone = getPromptFixtureValue(
    "dev_oauth_phone_link_phone",
  );
  const [phone, setPhone] = useState(promptFixturePhone);
  const [phoneRequiredError, setPhoneRequiredError] = useState(false);
  const phoneFieldShake = useRef(new Animated.Value(0)).current;
  const isFocused = useIsFocused();
  const [hasLeftHome, setHasLeftHome] = useState(false);
  const [isPromptReady, setIsPromptReady] = useState(promptFixtureEnabled);
  const [isPromptVisible, setIsPromptVisible] = useState(false);
  const hasPresentedPrompt = useRef(false);
  const [promptStep, setPromptStep] = useState<PromptStep>(() =>
    promptSuccessFixtureEnabled
      ? "success"
      : promptOtpFixtureEnabled
        ? "otp"
        : "phone",
  );
  const [otpMethodId, setOtpMethodId] = useState<string | null>(
    promptOtpFixtureEnabled ? "dev-otp-method" : null,
  );
  const [otpResetKey, setOtpResetKey] = useState(0);
  const [isOtpSubmitting, setIsOtpSubmitting] = useState(false);
  const [isOtpVerified, setIsOtpVerified] = useState(
    promptSuccessFixtureEnabled,
  );
  const [flowError, setFlowError] = useState<string | null>(null);
  const stytch = useStytch();
  const { user } = useStytchUser();
  const { patientId } = useApiClient();
  const { isLoading: isPatientLoading } = usePatient();
  const updatePatient = useUpdatePatient();
  const {
    isDismissed,
    dismiss,
    isLoading: isDismissalLoading,
  } = useDismissible(DISMISSAL_KEY);

  const isOAuthUser = hasGoogleOrAppleProvider(user?.providers);
  const hasVerifiedPhone =
    user?.phone_numbers.some((phone) => phone.verified) ?? false;
  // The profile's country, once it has loaded. The sign-in boot writes one
  // before this prompt appears, so the picker follows the profile and nothing
  // else; the default stands in only for the first paint.
  const { countryCode: profilePhoneCountry } = useUserCountry({
    supportedCountryCodes: SUPPORTED_PHONE_COUNTRY_CODES,
  });
  const [phoneCountry, setPhoneCountry] = useState<MoonPhoneCountry>(
    () => findMoonPhoneCountry(profilePhoneCountry) ?? DEFAULT_PHONE_COUNTRY,
  );

  useEffect(() => {
    const nextCountry = findMoonPhoneCountry(profilePhoneCountry);
    if (nextCountry) setPhoneCountry(nextCountry);
  }, [profilePhoneCountry]);

  useEffect(() => {
    if (promptFixtureEnabled) {
      setIsPromptReady(true);
      return;
    }

    if (!isFocused) {
      setHasLeftHome(true);
      return;
    }

    if (!isOAuthUser || isDismissed || isPromptReady) return;

    const delay = hasLeftHome ? PROMPT_RETURN_DELAY_MS : PROMPT_IDLE_DELAY_MS;
    const timer = setTimeout(() => setIsPromptReady(true), delay);

    return () => clearTimeout(timer);
  }, [
    hasLeftHome,
    isDismissed,
    isFocused,
    isOAuthUser,
    isPromptReady,
    promptFixtureEnabled,
  ]);

  useEffect(() => {
    if (!isFocused) {
      setIsPromptVisible(false);
      return;
    }

    if (
      !isPromptReady ||
      (isDismissed && !promptFixtureEnabled) ||
      (!isOAuthUser && !promptFixtureEnabled) ||
      hasPresentedPrompt.current
    ) {
      return;
    }

    hasPresentedPrompt.current = true;
    setIsPromptVisible(true);
  }, [
    isDismissed,
    isFocused,
    isOAuthUser,
    isPromptReady,
    promptFixtureEnabled,
  ]);

  const showPrompt =
    isFocused &&
    isPromptVisible &&
    (!isDismissed || promptFixtureEnabled) &&
    !isDismissalLoading &&
    !isPatientLoading &&
    // The deterministic visual fixture is intentionally patientless so the
    // product route can exercise the dialog without a test-user secret. The
    // production path still requires the authenticated patient record.
    (!!patientId || promptFixtureEnabled) &&
    (!hasVerifiedPhone ||
      isOtpSubmitting ||
      isOtpVerified ||
      promptStep === "success") &&
    (isOAuthUser || isPromptFixtureEnabled());

  const dismissPrompt = useCallback(() => {
    setIsPromptVisible(false);
    dismiss();
  }, [dismiss]);

  const shakePhoneField = useCallback(() => {
    phoneFieldShake.stopAnimation();
    phoneFieldShake.setValue(0);
    Animated.sequence([
      Animated.timing(phoneFieldShake, {
        toValue: -8,
        duration: 55,
        useNativeDriver: true,
      }),
      Animated.timing(phoneFieldShake, {
        toValue: 8,
        duration: 55,
        useNativeDriver: true,
      }),
      Animated.timing(phoneFieldShake, {
        toValue: -5,
        duration: 45,
        useNativeDriver: true,
      }),
      Animated.timing(phoneFieldShake, {
        toValue: 5,
        duration: 45,
        useNativeDriver: true,
      }),
      Animated.timing(phoneFieldShake, {
        toValue: 0,
        duration: 40,
        useNativeDriver: true,
      }),
    ]).start();
  }, [phoneFieldShake]);

  const handlePhoneChange = useCallback((next: string) => {
    setPhone(next);
    if (next) setPhoneRequiredError(false);
    setFlowError(null);
  }, []);

  const sendOtp = useCallback(async () => {
    if (!phone || isOtpSubmitting) return;

    setIsOtpSubmitting(true);
    setFlowError(null);
    setIsOtpVerified(false);
    try {
      const response = await stytch.otps.sms.send(phone, {
        expiration_minutes: 10,
      });
      setOtpMethodId(response.method_id);
      setOtpResetKey((current) => current + 1);
      setPromptStep("otp");
    } catch {
      setFlowError(t("home.oauth_phone_link.otp_send_failed"));
    } finally {
      setIsOtpSubmitting(false);
    }
  }, [isOtpSubmitting, phone, stytch.otps.sms, t]);

  const persistVerifiedPhone = useCallback(
    async (submittingAlready = false) => {
      if (!submittingAlready && isOtpSubmitting) return;

      if (!submittingAlready) {
        setIsOtpSubmitting(true);
        setFlowError(null);
      }

      try {
        await updatePatient.mutateAsync({ phoneNumber: phone });
        setPromptStep("success");
      } catch {
        setFlowError(t("home.oauth_phone_link.otp_save_failed"));
      } finally {
        if (!submittingAlready) setIsOtpSubmitting(false);
      }
    },
    [isOtpSubmitting, phone, t, updatePatient],
  );

  const verifyOtp = useCallback(
    async (code: string) => {
      if (!otpMethodId || code.length !== 6 || isOtpSubmitting) return;

      setIsOtpSubmitting(true);
      setFlowError(null);
      try {
        await stytch.otps.authenticate(code, otpMethodId, {
          session_duration_minutes: 60 * 24,
        });
      } catch {
        setFlowError(t("home.oauth_phone_link.otp_invalid_code"));
        setIsOtpSubmitting(false);
        return;
      }

      setIsOtpVerified(true);
      await persistVerifiedPhone(true);
      setIsOtpSubmitting(false);
    },
    [isOtpSubmitting, otpMethodId, persistVerifiedPhone, stytch.otps, t],
  );

  const resetPhoneEntry = useCallback(() => {
    if (isOtpSubmitting || isOtpVerified) return;

    setPromptStep("phone");
    setOtpMethodId(null);
    setOtpResetKey((current) => current + 1);
    setFlowError(null);
    setPhoneRequiredError(false);
  }, [isOtpSubmitting, isOtpVerified]);

  const addPhone = useCallback(() => {
    if (!phone) {
      setPhoneRequiredError(true);
      shakePhoneField();
      return;
    }
    void sendOtp();
  }, [phone, sendOtp, shakePhoneField]);

  return (
    <Dialog
      visible={showPrompt}
      onClose={dismissPrompt}
      closeButton="minimal"
      surfaceStyle={{
        backgroundColor: "rgba(20, 13, 9, 0.24)",
      }}
      contentStyle={{ position: "relative" }}
      testID="oauth-phone-link-prompt"
    >
      <View pointerEvents="none" style={StyleSheet.absoluteFill}>
        <Image
          source={ONBOARDING_CELLULAR_BACKGROUND}
          resizeMode="cover"
          style={[StyleSheet.absoluteFill, { width: "100%", height: "100%" }]}
        />
        <LinearGradient
          colors={[
            "rgba(21, 8, 2, 0.00)",
            "rgba(21, 8, 2, 0.00)",
            "rgba(14, 6, 2, 0.12)",
          ]}
          locations={[0, 0.48, 1]}
          style={StyleSheet.absoluteFill}
        />
        <BlurView
          intensity={24}
          tint="dark"
          experimentalBlurMethod="dimezisBlurView"
          style={StyleSheet.absoluteFill}
        />
        <View
          style={[
            StyleSheet.absoluteFill,
            { backgroundColor: "rgba(55, 42, 34, 0.12)" },
          ]}
        />
      </View>

      <TextingExchangeAnimation />

      <DialogHeader
        align="center"
        title={
          promptStep === "success"
            ? t("home.oauth_phone_link.phone_verified_title")
            : promptStep === "otp"
              ? t("home.oauth_phone_link.confirm_phone_number")
              : t("home.oauth_phone_link.chat_from_your_phone")
        }
        style={{ alignItems: "center", width: "100%", marginBottom: 10 }}
        titleStyle={{ color: semanticColors.onDark.text.primary }}
      />

      <Text
        className="font-dm-sans"
        style={{
          color: semanticColors.onDark.text.secondary,
          fontSize: 14,
          lineHeight: 21,
          textAlign: "center",
        }}
      >
        {promptStep === "success"
          ? t("home.oauth_phone_link.phone_verified_subtitle")
          : promptStep === "otp"
            ? t("home.oauth_phone_link.otp_subtitle", {
                params: { phone },
              })
            : t("home.oauth_phone_link.connect_account_to_texting_concierge")}
      </Text>

      {promptStep === "success" ? (
        <View
          testID="oauth-phone-link-success"
          style={{ width: "100%", marginTop: 24 }}
        >
          <View
            style={{
              width: 52,
              height: 52,
              alignSelf: "center",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: 26,
              backgroundColor: semanticColors.onDark.fill.strong,
              borderWidth: 1,
              borderColor: "rgba(255, 239, 225, 0.28)",
            }}
          >
            <Text
              className="font-dm-sans"
              style={{
                color: semanticColors.onDark.text.primary,
                fontSize: 26,
                lineHeight: 30,
              }}
            >
              ✓
            </Text>
          </View>
          <View style={{ marginTop: 22 }}>
            <OnboardingPrimaryButton
              title={t("home.oauth_phone_link.phone_verified_done")}
              onPress={dismissPrompt}
            />
          </View>
        </View>
      ) : promptStep === "otp" ? (
        <>
          <View
            testID="oauth-phone-link-otp"
            style={{ width: "100%", marginTop: 20 }}
          >
            <OTPInput
              key={otpResetKey}
              length={6}
              onChange={() => setFlowError(null)}
              onComplete={(code) => void verifyOtp(code)}
              appearance="glass"
            />
          </View>

          {isOtpSubmitting && (
            <LoadingState
              appearance="dark"
              style={{ paddingVertical: 0, marginTop: 14 }}
            />
          )}

          {flowError && (
            <FieldMessage onDark centered style={{ marginTop: 12 }}>
              {flowError}
            </FieldMessage>
          )}

          <TextActionButton
            label={t("home.oauth_phone_link.otp_resend")}
            action="home.oauth_phone_link.otp_resend"
            onPress={() => void sendOtp()}
            disabled={isOtpSubmitting || isOtpVerified}
            tone="onDark"
            size="sm"
            icon={RotateCcw}
            centered
            style={{ marginTop: 12 }}
          />

          {isOtpVerified ? (
            <View style={{ marginTop: 18 }}>
              <OnboardingPrimaryButton
                title={t("home.oauth_phone_link.save_verified_phone")}
                onPress={() => void persistVerifiedPhone()}
                disabled={isOtpSubmitting}
                showArrow
              />
            </View>
          ) : (
            <TextActionButton
              label={t("home.oauth_phone_link.change_phone_number")}
              action="home.oauth_phone_link.change_phone_number"
              onPress={resetPhoneEntry}
              disabled={isOtpSubmitting}
              tone="onDark"
              size="sm"
              icon={Phone}
              centered
              style={{ marginTop: 4 }}
            />
          )}
        </>
      ) : (
        <>
          <Animated.View
            style={{
              marginTop: 16,
              transform: [{ translateX: phoneFieldShake }],
            }}
          >
            <MoonPhoneField
              value={phone}
              onChange={handlePhoneChange}
              defaultCountry={phoneCountry.iso}
              placeholder={t("home.oauth_phone_link.phone_placeholder")}
              error={
                phoneRequiredError
                  ? t("home.oauth_phone_link.phone_required")
                  : undefined
              }
              icon={null}
              appearance="glass"
              testID="oauth-phone-link-phone-field"
              countrySelectorTestID="oauth-phone-link-country-picker"
              countrySearchTestID="oauth-phone-link-country-search"
            />
          </Animated.View>

          {flowError && (
            <FieldMessage onDark centered style={{ marginTop: 12 }}>
              {flowError}
            </FieldMessage>
          )}

          <View style={{ marginTop: 18 }}>
            <OnboardingPrimaryButton
              title={t("home.oauth_phone_link.add_phone")}
              onPress={addPhone}
              disabled={isOtpSubmitting}
              showArrow
            />
          </View>
        </>
      )}
    </Dialog>
  );
}
